import { ActionIcon, Box, Group, Loader, Stack, Text, Tooltip } from '@mantine/core';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { IconPhoto, IconTrash, IconUpload, IconX } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { adminApi } from '@/api/admin';
import { errorMessage } from '@/api/http';
import type { ImageKind, ImageRef } from '@/api/types';
import { imageUrl } from '@/lib/image';

const ACCEPT = IMAGE_MIME_TYPE.filter((t) => ['image/png', 'image/jpeg', 'image/webp'].includes(t));
const MAX_BYTES = 10 * 1024 * 1024;

interface Props {
  kind: ImageKind;
  label: string;
  hint: string;
  ratio: number;
  /** Preview box size in CSS px. */
  previewWidth: number;
  value: ImageRef | null | undefined;
  onChange: (v: ImageRef | null) => void;
  disabled?: boolean;
}

/** Drop an image → POST /v1/admin/images → preview via the returned template. */
export function ImageSlot({
  kind,
  label,
  hint,
  ratio,
  previewWidth,
  value,
  onChange,
  disabled,
}: Props) {
  const upload = useMutation({
    mutationFn: (file: File) => adminApi.uploadImage(file, kind),
    onSuccess: (ref) => onChange(ref),
    onError: (e) =>
      notifications.show({
        color: 'coral',
        title: `${label} not uploaded`,
        message: errorMessage(e),
      }),
  });
  const w = previewWidth;
  const h = Math.round(w / ratio);
  const src = imageUrl(value, w * 2, h * 2);

  return (
    <Stack gap={6}>
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <div>
          <Text size="sm" fw={600}>
            {label}
          </Text>
          <Text size="xs" c="dimmed">
            {hint}
          </Text>
        </div>
        {value && !disabled && (
          <Tooltip label={`Remove ${label.toLowerCase()}`}>
            <ActionIcon
              variant="subtle"
              color="coral"
              aria-label={`Remove ${label}`}
              onClick={() => onChange(null)}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
      <Dropzone
        onDrop={(files) => files[0] && upload.mutate(files[0])}
        onReject={(rej) =>
          notifications.show({
            color: 'coral',
            message:
              rej[0]?.errors[0]?.code === 'file-too-large'
                ? 'Images must be 10 MB or smaller.'
                : 'Use a JPEG, PNG or WebP image.',
          })
        }
        accept={ACCEPT}
        maxSize={MAX_BYTES}
        multiple={false}
        disabled={disabled || upload.isPending}
        p={0}
        radius="md"
        aria-label={`Upload ${label}`}
        style={{ width: '100%', maxWidth: w }}
      >
        <Box
          style={{
            aspectRatio: String(ratio),
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 12,
          }}
        >
          {src ? (
            <img
              src={src}
              alt={`${label} preview`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Stack
              align="center"
              justify="center"
              h="100%"
              gap={4}
              p="xs"
              style={{ pointerEvents: 'none' }}
            >
              <Dropzone.Accept>
                <IconUpload size={24} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={24} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconPhoto size={24} />
              </Dropzone.Idle>
              <Text size="xs" ta="center" c="dimmed">
                Drop an image or click
              </Text>
            </Stack>
          )}
          {upload.isPending && (
            <Box
              pos="absolute"
              inset={0}
              style={{ display: 'grid', placeItems: 'center', background: 'rgba(255,250,243,.7)' }}
            >
              <Loader size="sm" aria-label="Uploading" />
            </Box>
          )}
        </Box>
      </Dropzone>
      {src && (
        <Text size="xs" c="dimmed">
          Drop a new file on the image to replace it.
        </Text>
      )}
    </Stack>
  );
}
